import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Card from '@amzn/meridian/card';
import Checkbox from '@amzn/meridian/checkbox';
import getDateInUTC from '@components/agile/utils/GetDateUtils';

interface AtAcrossZoneModalsProps {
  openConfirmationModal: boolean;
  openErrorModal: boolean;
  successful: boolean;
  agreementCheckBox: boolean;
  minDraftStartTime: number;
  inputAsins: string[];
  promoteDraftErrorMsg?: string;
  setOpenConfirmationModal: (value: boolean) => void;
  setOpenErrorModal: (value: boolean) => void;
  setAgreementCheckBox: (value: boolean) => void;
  handleConfirmPromotion: () => void;
  onClose: () => void;
}

const AtAcrossZoneModals: React.FC<AtAcrossZoneModalsProps> = ({
  openConfirmationModal,
  openErrorModal,
  successful,
  agreementCheckBox,
  minDraftStartTime,
  inputAsins,
  promoteDraftErrorMsg,
  setOpenConfirmationModal,
  setOpenErrorModal,
  setAgreementCheckBox,
  handleConfirmPromotion,
  onClose,
}) => (
  <>
    {/* Confirmation Modal */}
    <Modal
      open={openConfirmationModal}
      scrollContainer="viewport"
      aria-describedby="confirmation-modal"
      onClose={() => setOpenConfirmationModal(false)}
    >
      <Column spacingInset="300">
        <Alert size="large" type="warning">
          <h3>You are about to promote Draft Zone to live status. Would you like to proceed?</h3>
        </Alert>
        <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
          <Card>
            Number of ASINs that will be promoted to live:{' '}
            <Text color="primary" type="h300">
              {inputAsins.length}
            </Text>
            Zone Map Start Date:{' '}
            <Text color="primary" type="h300">
              {getDateInUTC(minDraftStartTime)}
            </Text>
            <Row spacingInset="300" spacing="400">
              <Alert size="large" type="warning">
                <Checkbox checked={agreementCheckBox} onChange={setAgreementCheckBox}>
                  I understand that promoting the draft zone map to live will create a new zone map with live date::{' '}
                  {getDateInUTC(minDraftStartTime)} with {inputAsins.length} ASINs. End all previous live zones and
                  selections. Remove any future live zones.
                </Checkbox>
                <Text>
                  <strong>Important: </strong>
                  Use the &quot;Assortment Search&quot; page to verify assortment schedules.
                </Text>
              </Alert>
            </Row>
            <ModalFooter>
              <Row alignmentHorizontal="end" widths="fit" spacingInset="300" spacing="400">
                <Button
                  type="secondary"
                  size="small"
                  onClick={() => {
                    setOpenConfirmationModal(false);
                    setAgreementCheckBox(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" size="small" onClick={handleConfirmPromotion} disabled={!agreementCheckBox}>
                  Confirm
                </Button>
              </Row>
            </ModalFooter>
          </Card>
        </Row>
      </Column>
    </Modal>

    {/* Error Modal */}
    <Modal
      open={openErrorModal}
      scrollContainer="viewport"
      aria-describedby="error-modal"
      onClose={() => {
        setOpenErrorModal(false);
      }}
    >
      <Column spacingInset="300">
        <Alert size="xlarge" type="error">
          <h2>Error Message</h2>
        </Alert>
        <Text>{promoteDraftErrorMsg}</Text>
        <Row alignmentHorizontal="center" spacingInset="300">
          <Button onClick={() => setOpenErrorModal(false)}>Close</Button>
        </Row>
      </Column>
    </Modal>

    {/* Success Modal */}
    <Modal open={successful} scrollContainer="viewport" aria-describedby="success-modal" onClose={onClose}>
      <Alert type="success" size="xlarge">
        Draft Zone Successfully Promoted to Live
      </Alert>
    </Modal>
  </>
);

export default AtAcrossZoneModals;
